import type { ReactNode } from "react";
import { useContext, createContext, useState } from "react";

import type { Flipper } from "@shared/types/flipper";

type Flippers = Flipper[];
type FlipperResults = { name: string; result: boolean }[];

interface SelectedFlipper {
  name: string;
  enabled: boolean;
}

interface FlippersData {
  flippers: Flippers;
  untrackedExperimentFeatures: string[];
  selectedFlipper: SelectedFlipper | null;
  setFlipper: (flipper: Flipper, active: boolean) => void;
  setFlippers: (flippers: FlipperResults) => void;
  setSelectedFlipper: (value: SelectedFlipper | null) => void;
}

const FlippersContext = createContext<FlippersData>({
  flippers: [],
  untrackedExperimentFeatures: [],
  selectedFlipper: null,
  setFlipper: () => undefined,
  setFlippers: () => undefined,
  setSelectedFlipper: () => undefined,
});

interface Props {
  children: ReactNode;
  flippers: Flippers;
  untrackedExperimentFeatures?: string[];
}

const FlippersProvider = ({ flippers, untrackedExperimentFeatures = [], children }: Props) => {
  const [activeFlippers, setActiveFlippers] = useState(flippers);
  const [selectedFlipper, setSelectedFlipper] = useState<SelectedFlipper | null>(null);

  const setFlipper = (flipper: Flipper, active: boolean) => {
    if (active && !activeFlippers.includes(flipper)) {
      setActiveFlippers([...activeFlippers, flipper]);
    } else if (!active && activeFlippers.includes(flipper)) {
      const newFlippers = [...activeFlippers];
      newFlippers.splice(newFlippers.indexOf(flipper), 1);

      setActiveFlippers(newFlippers);
    }
  };

  const setFlippers = (flipperResults: FlipperResults) => {
    const newFlippers = flipperResults
      .filter(flipper => flipper.result)
      .map(({ name }) => name as Flipper);
    setActiveFlippers(newFlippers);
  };

  return (
    <FlippersContext
      value={{
        flippers: activeFlippers,
        untrackedExperimentFeatures,
        setFlipper,
        setFlippers,
        selectedFlipper,
        setSelectedFlipper,
      }}
    >
      {children}
    </FlippersContext>
  );
};

const useFlippers = (...args: Flipper[]): boolean[] => {
  const { flippers } = useContext(FlippersContext);

  return args.map(flipper => flippers.includes(flipper));
};

export { FlippersContext, FlippersProvider, useFlippers };
